import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { actesHistory, actesUploadFile, type ActesHistoryRow } from '@/integrations/actesApi';
import { DocViewer } from './DocViewer';
import { actesEn } from '@/lib/actesCountry';

/**
 * DERNIERS SCANS — le registre des dépôts, à côté des demandes.
 *
 * The reception log is not only the reception page's business. What the team
 * actually asks, standing in front of Demandes, is "did that scan arrive, and
 * did it land on a dossier or is it still loose?" — so the log lives here too,
 * with the one filter that answers it: with a dossier, without one, or both.
 *
 * A scan without a dossier is the interesting one: it came in and nobody has
 * said whose it is yet. That is why it is called out in amber rather than
 * being one grey row among many.
 *
 * A row opens the scan itself; the second button jumps to the demande it was
 * filed on, when there is one.
 */
type Scope = 'all' | 'with' | 'without';

export function ScansList({ lang, onOpenDemande }: {
  lang: Lang;
  /** Jump to the demande the scan was filed on. */
  onOpenDemande?: (acteId: string) => void;
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const [scope, setScope] = useState<Scope>('all');
  const [q, setQ] = useState('');
  const [day, setDay] = useState('');
  const [view, setView] = useState<string | null>(null);

  const rows = useQuery<ActesHistoryRow[]>({
    queryKey: ['actes', 'scans'],
    queryFn: ({ signal }) => actesHistory(signal),
    staleTime: 60_000,
  });

  const dayKey = (iso: string) => iso.slice(0, 10);
  const all = rows.data ?? [];
  const days = useMemo(
    () => [...new Set(all.map((h) => dayKey(h.created_at)))].sort((a, b) => b.localeCompare(a)),
    [all],
  );
  const nWith = all.filter((h) => h.dossier).length;

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all.filter((h) => {
      if (scope === 'with' && !h.dossier) return false;
      if (scope === 'without' && h.dossier) return false;
      if (day && dayKey(h.created_at) !== day) return false;
      if (!needle) return true;
      return [h.dossier, h.file_name, h.detected_type, h.uploaded_by]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(needle));
    });
  }, [all, scope, q, day]);

  const SCOPES: { id: Scope; label: string; n: number }[] = [
    { id: 'all', label: T('Tous', 'All'), n: all.length },
    { id: 'with', label: T('Avec dossier', 'With dossier'), n: nWith },
    { id: 'without', label: T('Sans dossier', 'No dossier'), n: all.length - nWith },
  ];

  return (
    <section className="actes-panel">
      <div className="actes-tabline">
        <h3 className="actes-h3">{T('Derniers scans', 'Latest scans')}</h3>
        <span className="actes-count">{shown.length}</span>
      </div>

      <div className="actes-toolbar">
        <div className="res-chips">
          {SCOPES.map((s) => (
            <button key={s.id} className={`res-chip ${scope === s.id ? 'on' : ''}`} onClick={() => setScope(s.id)}>
              {s.label} <em>{s.n}</em>
            </button>
          ))}
        </div>
        <div className="actes-filters">
          <label className="actes-find wide">
            <KBIcon name="search" size={14} />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={T('Dossier, fichier, type ou personne…', 'Dossier, file, type or person…')} />
            {q && <button onClick={() => setQ('')} aria-label="clear">×</button>}
          </label>
          <label className="actes-field inline">
            <span>{T('Jour', 'Day')}</span>
            <select value={day} onChange={(e) => setDay(e.target.value)}>
              <option value="">{T('Tous', 'All')}</option>
              {days.map((d) => (
                <option key={d} value={d}>{new Date(`${d}T00:00:00`).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB')}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {rows.isLoading ? (
        <p className="muted-note">{T('Chargement…', 'Loading…')}</p>
      ) : shown.length === 0 ? (
        <div className="empty-state">
          <div className="ico">📥</div>
          <h3>{T('Aucun scan', 'No scan')}</h3>
        </div>
      ) : (
        <ul className="actes-scans">
          {shown.map((h) => (
            <li key={h.id} className={h.dossier ? '' : 'loose'}>
              <button className="actes-scan-open" disabled={!h.has_file}
                onClick={() => { if (h.has_file) setView(h.id); }}
                title={h.has_file ? T('Voir le scan', 'View the scan') : T('Fichier absent', 'No file')}>
                <KBIcon name="documents" size={15} />
              </button>
              <span className="actes-list-main">
                <strong>{h.dossier ?? h.file_name ?? '—'}</strong>
                <em>
                  {[h.detected_type,
                    h.dossier ? h.file_name : null,
                    h.uploaded_by,
                    new Date(h.created_at).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-GB'),
                  ].filter(Boolean).join(' · ')}
                </em>
              </span>
              <span className={`actes-tag ${h.dossier ? 'c-emerald' : 'c-amber'}`}>
                {h.dossier ? T('rattaché', 'filed') : T('sans dossier', 'loose')}
              </span>
              {h.acte_request_id && onOpenDemande && (
                <button className="actes-row-act" title={T('Ouvrir la demande', 'Open the request')}
                  onClick={() => onOpenDemande(h.acte_request_id as string)}>
                  <KBIcon name="arrow" size={13} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {view && <DocViewer lang={lang} load={() => actesUploadFile(view)} onClose={() => setView(null)} />}
    </section>
  );
}
